import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Coins, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getCreditTransactions, type CreditTransaction } from '@/api/client';

const TYPE_COLORS: Record<string, string> = {
  consume: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
  recharge: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  admin_adjust: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
  signup_bonus: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
  refund: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
};

export default function CreditLogPage() {
  const [items, setItems] = React.useState<CreditTransaction[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const pageSize = 20;
  const navigate = useNavigate();

  // 返回：优先回退到进入本页前的状态；无历史记录时回首页
  const handleBack = React.useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  }, [navigate]);

  const load = React.useCallback(async (p: number) => {
    setLoading(true);
    setError('');
    try {
      const data = await getCreditTransactions(p, pageSize);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(p);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load(1);
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">积分消耗日志</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            全部积分流水明细：消耗、充值、管理员调整等，共 {total} 条
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleBack} title="返回上一页">
          <ChevronLeft className="h-4 w-4" />
          返回
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Coins className="h-4 w-4 text-amber-500" />
            <CardTitle className="text-base">流水记录</CardTitle>
            <CardDescription>倒序排列</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-3 p-6">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />
              ))}
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 px-6 py-8 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Coins className="mb-3 h-8 w-8" />
              <p className="text-sm">暂无积分流水</p>
            </div>
          ) : (
            <div className="divide-y">
              {items.map((it) => (
                <div key={it.id} className="flex items-center gap-4 px-6 py-3.5">
                  <div className="w-36 shrink-0">
                    <div className="text-xs font-medium text-muted-foreground">{it.created_at}</div>
                  </div>
                  <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${TYPE_COLORS[it.type] || 'bg-accent/40 text-muted-foreground'}`}>
                    {it.type_label}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{it.item}</div>
                    {it.detail && (
                      <div className="truncate text-xs text-muted-foreground">{it.detail}</div>
                    )}
                  </div>
                  <div className={`shrink-0 text-sm font-semibold tabular-nums ${it.amount >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                    {it.amount >= 0 ? '+' : ''}{it.amount}
                  </div>
                  <div className="w-24 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                    余额 {it.balance_after}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
        {/* 分页 */}
        {totalPages > 1 && (
          <CardContent className="border-t">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                第 {page} / {totalPages} 页
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  disabled={page <= 1 || loading}
                  onClick={() => load(page - 1)}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  上一页
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  disabled={page >= totalPages || loading}
                  onClick={() => load(page + 1)}
                >
                  下一页
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
